/* global __SERVER__ */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { func, object, array, string, bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import config from '@dragonfruit/config';
import ContextStore from '@dragonfruit/services/createContext.js';

import MainLayout from '@dragonfruit/layouts/MainLayout';

import SEOHead from '@dragonfruit/components/SEOHead';
import PromotionBanner from '@dragonfruit/components/PromotionBanner';

import {
  getPricingBanner,
  fetchPromotionBanner,
  getPromotionBannerIsOpen,
} from '@dragonfruit/redux/promotionBanner';
import { goToPurchasePage } from '@dragonfruit/redux/navigation';
import { fetchPremiumPageFlow } from '@dragonfruit/sagas/premiumPage';

import HeroSection from './components/HeroSection';
import SectionNavigation from './components/SectionNavigation';

import SubSection from './components/SubSection';
import BundleBanners from './components/BundleBanners';
import SubscriptionSection from './components/SubscriptionSection';
import LiveSection from './components/LiveSection';
import Token from './components/Token';
import TokenPack from './components/TokenPack';
import PremiumFAQ from './components/PremiumFAQ';
import SecurePaymentStatement from './components/SecurePaymentStatement';
import ProductFeatures from './components/ProductFeatures';
import ProductCompare from './components/ProductCompare';
import TestimonialBlock from './components/TestimonialBlock';
import PromotionBlock from './components/PromotionBlock';
import usePromotionBannerCallback from '@dragonfruit/hooks/usePromotionBannerCallback';

import styles from './Premium.scss';
import { PRICING } from '@dragonfruit/constants/promotionPage';
import { DEFAULT_ACTIVE_GROUP_IS_YEARLY } from '@dragonfruit/constants/premiumPage';

import miniClassNavIcon from './images/navigators/ic-anchor-miniclass.svg';
import subscriptionNavIcon from './images/navigators/ic-anchor-subscription.svg';
import tokenNavIcon from './images/navigators/ic-anchor-token.svg';
import liveNavIcon from './images/navigators/ic-anchor-live.svg';
import bundleNavIcon from './images/navigators/ic-anchor-bundle.svg';

import miniClassIcon from './images/navigators/ic-section-miniclass.svg';
import subscriptionIcon from './images/navigators/ic-section-subscription.svg';
import tokenIcon from './images/navigators/ic-section-token.svg';
import liveIcon from './images/navigators/ic-section-live.svg';

import {
  BUNDLE_BANNERS,
  TOKENS,
  TOKEN_PACKS,
  WEB_SUBSCRIPTION_GROUPS,
  MINI_CLASSES,
  LIVE_GROUPS,
} from '@dragonfruit/constants/premiumPage';
import {
  headerHeight,
  bannerHeight,
} from '@dragonfruit/constants/elementHeight';
import { YEARLY } from '@dragonfruit/constants/contractPeriods';

const mapStateToProps = state => ({
  region: state.locale.region,
  userRegion: state.profile.data.region,
  promotionBanner: getPricingBanner(state),
  structureDataList: state.premiumPage.structureDataList,
  sectionList: state.premiumPage.sections,
  bundleBannerList: state.premiumPage.bundleBanners,
  webSubscriptionGroupMapping: state.premiumPage.webSubscriptionGroupMapping,
  liveGroups: state.premiumPage.liveGroups,
  tokenList: state.premiumPage.tokens,
  tokenPackList: state.premiumPage.tokenPacks,
  miniClassList: state.premiumPage.miniClasses,
  promotionBannerIsOpen: getPromotionBannerIsOpen(state),
});

function Premium(
  {
    fetchPromotionBanner,
    promotionBannerIsOpen,
    promotionBanner,
    sectionList,
    bundleBannerList,
    webSubscriptionGroupMapping,
    structureDataList,
    tokenList,
    tokenPackList,
    miniClassList,
    liveGroups,
    t,
    region,
    goToPurchasePage,
    userRegion,
    location,
  },
  { urlLocale }
) {
  const [activeName, setActiveName] = useState('');

  useEffect(() => {
    fetchPromotionBanner(PRICING);
  }, [fetchPromotionBanner]);

  const {
    bannerBtnText,
    bannerDescription,
    bannerCallback,
  } = usePromotionBannerCallback(promotionBanner);

  const defaultSubscriptionGroupKey = DEFAULT_ACTIVE_GROUP_IS_YEARLY.includes(
    userRegion || region
  )
    ? YEARLY
    : undefined;

  const sectionComponentList = useMemo(() => {
    const renderTokenList = (token, tokenIdx) => (
      <Token {...token} key={tokenIdx} />
    );

    const renderTokenPackList = (tokenPack, tokenPackIdx) => (
      <TokenPack {...tokenPack} key={tokenPackIdx} />
    );

    const renderMiniClassList = (miniClass, miniClassIdx) => (
      <Token {...miniClass} isMiniClass key={miniClassIdx} />
    );

    return {
      [BUNDLE_BANNERS]: {
        heading: t('premium.bundleBanners.title'),
        showSectionHeading: false,
        navIcon: bundleNavIcon,
        renderChildren: () => <BundleBanners banners={bundleBannerList} />,
      },
      [TOKENS]: {
        heading: t('premium.tokens.title'),
        description: t('premium.tokens.description'),
        icon: tokenIcon,
        navIcon: tokenNavIcon,
        link: t('premium.tokens.link'),
        linkText: t('premium.tokens.linkText'),
        renderChildren: () => tokenList.map(renderTokenList),
      },
      [TOKEN_PACKS]: {
        heading: t('premium.tokens.title'),
        description: t('premium.tokens.description'),
        icon: tokenIcon,
        navIcon: tokenNavIcon,
        link: t('premium.tokens.link'),
        linkText: t('premium.tokens.linkText'),
        renderChildren: () => tokenPackList.map(renderTokenPackList),
      },
      [LIVE_GROUPS]: {
        heading: t('premium.live_groups.title'),
        description: t('premium.live_groups.description'),
        icon: liveIcon,
        navIcon: liveNavIcon,
        renderChildren: () => <LiveSection liveGroups={liveGroups} />,
      },
      [WEB_SUBSCRIPTION_GROUPS]: {
        heading: t('premium.subscriptions.title'),
        description: t('premium.subscriptions.description'),
        icon: subscriptionIcon,
        navIcon: subscriptionNavIcon,
        link: t('premium.subscriptions.link'),
        linkText: t('premium.subscriptions.linkText'),
        renderChildren: () => (
          <SubscriptionSection
            defaultSubscriptionGroupKey={defaultSubscriptionGroupKey}
            webSubscriptionGroupMapping={webSubscriptionGroupMapping}
            location={location}
          />
        ),
      },
      [MINI_CLASSES]: {
        heading: t('premium.miniClass.title'),
        description: t('premium.miniClass.description'),
        icon: miniClassIcon,
        navIcon: miniClassNavIcon,
        link: t('premium.miniClass.link'),
        linkText: t('premium.miniClass.linkText'),
        renderChildren: () => miniClassList.map(renderMiniClassList),
        className: 'miniClasses',
      },
    };
  }, [
    t,
    bundleBannerList,
    tokenList,
    tokenPackList,
    liveGroups,
    defaultSubscriptionGroupKey,
    webSubscriptionGroupMapping,
    location,
    miniClassList,
  ]);

  const currentSectionComponents = useMemo(
    () =>
      sectionList.map(sectionName => ({
        ...sectionComponentList[sectionName],
        sectionName,
      })),
    [sectionComponentList, sectionList]
  );

  const handleScrollToSection = sectionName => {
    const targetSectionElement = document.getElementById(sectionName);
    const elementPosition = targetSectionElement.offsetTop;
    const headerOffsetHeight = headerHeight + bannerHeight;
    const targetPosition = elementPosition - headerOffsetHeight;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  };

  const handleClickNavigation = useCallback(
    sectionName => {
      handleScrollToSection(sectionName);
      if (sectionName === activeName) return;
      setActiveName(sectionName);
    },
    [activeName]
  );

  const handleSectionInView = useCallback(
    sectionName => setActiveName(sectionName),
    []
  );

  const showPromotionBanner = promotionBannerIsOpen && promotionBanner;

  const structureData = structureDataList.map(plan => ({
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: plan.name,
    description: plan.description,
    sku: plan.sku,
    brand: {
      '@type': 'Brand',
      name: 'Snapask',
    },
    offers: {
      '@type': 'Offer',
      url: `${config.SNAPASK_SITE_ENDPOINT}/${urlLocale}/premium`,
      priceCurrency: plan.currency,
      price: plan.price,
      availability: 'http://schema.org/InStock',
    },
  }));

  const seoComponent = (
    <SEOHead
      path="premium"
      title={t('seo:seo.premium.title')}
      description={t('seo:seo.premium.description')}
      structData={structureData}
    />
  );

  if (__SERVER__) {
    return seoComponent;
  }

  return (
    <MainLayout showFooter>
      {seoComponent}
      {showPromotionBanner && (
        <PromotionBanner
          btnText={bannerBtnText}
          descr={bannerDescription}
          onClick={bannerCallback}
        />
      )}
      <main className={styles.root}>
        <HeroSection>
          <SectionNavigation
            activeName={activeName}
            onClickNavigationItem={handleClickNavigation}
            navigatorList={currentSectionComponents}
          />
        </HeroSection>

        <ContextStore.Provider
          value={{
            goToPurchasePage,
            urlLocale,
          }}
        >
          {currentSectionComponents.map((sectionData, sectionIdx) => {
            const { sectionName } = sectionData;
            return (
              <SubSection
                name={sectionName}
                {...sectionData}
                inView={handleSectionInView}
                className={styles[`section_${sectionData.className}`]}
                key={sectionIdx}
              />
            );
          })}
        </ContextStore.Provider>

        <ProductFeatures />
        <ProductCompare />
        <TestimonialBlock />
        <PremiumFAQ />
        <SecurePaymentStatement />
        <PromotionBlock />
      </main>
    </MainLayout>
  );
}

Premium.prefetch = () => [fetchPremiumPageFlow];

Premium.propTypes = {
  bundleBannerList: array.isRequired,
  fetchPromotionBanner: func.isRequired,
  goToPurchasePage: func.isRequired,
  liveGroups: object.isRequired,
  location: object.isRequired,
  miniClassList: array.isRequired,
  promotionBannerIsOpen: bool.isRequired,
  region: string.isRequired,
  sectionList: array.isRequired,
  structureDataList: array.isRequired,
  t: func.isRequired,
  tokenList: array.isRequired,
  tokenPackList: array.isRequired,
  promotionBanner: object,
  userRegion: string,
  webSubscriptionGroupMapping: object,
};

Premium.defaultProps = {
  promotionBanner: undefined,
  webSubscriptionGroupMapping: {},
};

Premium.contextTypes = {
  urlLocale: string,
};

export default compose(
  translate(['premium', 'seo']),
  connect(mapStateToProps, {
    fetchPromotionBanner,
    goToPurchasePage,
  })
)(Premium);



// WEBPACK FOOTER //
// ./app/containers/Premium/Premium.jsx